import AccessControl from '@/components/access-control/AccessControl.component';
import CorrectedIcon from '@/components/icon/CorrectedIcon';
import { useOpsTooling } from '@/contexts/OpsToolingContext';
import usePayrollSetups from '@/hooks/usePayrollSetups';
import { FeatureLevelPermissions } from '@/models/UserPermissions.model';
import { StartOffCycleContribution } from '@/routes/plans/plan-contribution-submission';
import { OpsToolingType } from '@/services/payroll-integrations.service';
import { PlanService } from '@/services/Plan.service';
import formatters from '@/utils/Formatters';
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  MoreVert,
  PriceCheckOutlined
} from '@mui/icons-material';
import {
  Box,
  Card,
  Chip,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  styled,
  Theme,
  Tooltip,
  Typography
} from '@mui/material';
import { grey } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';

import Decimal from 'decimal.js';
import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { ContributionsFilter } from './ContributionFilter.component';
import { ContributionRowDetails } from './ContributionRowDetails.component';
import { SubmissionActions } from './SubmissionActions.component';

const isTest = process.env.NODE_ENV === 'test';

const useStyles = makeStyles((theme: Theme) => ({
  anchorIcon: {
    marginLeft: theme.spacing(2)
  },
  titleBox: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(2.5, 2)
  }
}));

const StyledChip = styled(Chip)((props: { type: string }) => ({
  fontWeight: 500,
  ...(props.type === 'completed'
    ? {
        backgroundColor: '#edf7ed',
        color: '#3B873E'
      }
    : {}),
  ...(props.type === 'upcoming'
    ? {
        backgroundColor: '#eeeeee',
        color: '#757575'
      }
    : {}),
  ...(props.type === 'missing'
    ? {
        backgroundColor: '#fff5e5',
        color: '#C77700'
      }
    : {}),
  ...(props.type === 'processing'
    ? {
        backgroundColor: '#e3f2fd',
        color: '#2196F3'
      }
    : {})
}));

interface PlanContributionsTabProps {
  sponsorPlanId: number;
  sponsorId?: number;
}

const PlanContributionsTab: React.FC<PlanContributionsTabProps> = props => {
  const classes = useStyles();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [searchParams] = useSearchParams();

  const queryParams = useMemo(
    () => ({
      flowSubtypes: searchParams.get('flowSubtypes'),
      maxPayDate: searchParams.get('maxPayDate'),
      maxTotal: searchParams.get('maxTotal'),
      minPayDate: searchParams.get('minPayDate'),
      minTotal: searchParams.get('minTotal'),
      payGroups: searchParams.get('payGroups'),
      payrollDueStatuses: searchParams.get('payrollDueStatuses'),
      statuses: searchParams.get('statuses'),
      ucids: searchParams.get('ucids')
    }),
    [searchParams]
  );

  const contributions = useQuery(
    [
      PlanService.getPlanContributions.name,
      props.sponsorPlanId,
      Object.values(queryParams)
    ],
    async () =>
      PlanService.getPlanContributions(props.sponsorPlanId, {
        ...queryParams
      }),
    {
      keepPreviousData: true
    }
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'transaction',
        flex: 1,
        headerName: 'Transaction',
        minWidth: 180,
        renderCell: params => {
          const flowSubtype =
            {
              correction: 'Correction',
              loan_payment: 'Loan Repayment',
              'off-cycle_supplemental_pay': 'Off-Cycle',
              regular: 'Regular'
            }[params.row?.key?.flowSubtype] || '--';

          return (
            <Stack display='flex' flexDirection='row' gap={1} py={1}>
              <Stack>
                <Typography>
                  {params.row?.key?.ucid ? (
                    <Link
                      alignItems='center'
                      component={RouterLink}
                      display='flex'
                      gap={1.25}
                      onClick={params.row?.navigate}
                      to={`/plans/${params.row?.key?.sponsorPlanId}/contributions/${params.row?.key?.ucid}/overview`}
                      underline='hover'>
                      {params.row?.key?.expectedPayrollDate}
                    </Link>
                  ) : (
                    params.row?.key?.expectedPayrollDate
                  )}
                </Typography>
                <Typography color='grey' variant='body2'>
                  {flowSubtype}
                </Typography>
              </Stack>
              <Stack display='flex' flexDirection='row' gap={0.5} pt={0.5}>
                {params.row.contributionMeta?.hasLostGains && (
                  <Tooltip color='primary' title='Lost Gain'>
                    <PriceCheckOutlined fontSize='small' />
                  </Tooltip>
                )}
                {params.row.contributionMeta?.hasCorrections && (
                  <Tooltip
                    color='primary'
                    title='This contribution has corrections'>
                    <CorrectedIcon fontSize='small' />
                  </Tooltip>
                )}
                {params.row?.contributionMeta?.hasMatchRemediations && (
                  <Tooltip
                    color='primary'
                    title='This contribution has match remediations'>
                    <CorrectedIcon fontSize='small' />
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          );
        },
        sortable: true,
        valueGetter: params => new Date(params.row?.key?.expectedPayrollDate)
      },
      {
        field: 'status',
        flex: 1,
        headerName: 'Status',
        minWidth: 200,
        renderCell: params => {
          const isUpcoming = ['Due', 'Upcoming'].includes(params.value);

          const isMissing = ['Late', 'Late Critical', 'Missing'].includes(
            params.value
          );

          const isCompleted = params.value === 'COMPLETED';

          return (
            <StyledChip
              label={params.formattedValue}
              type={
                isUpcoming
                  ? 'upcoming'
                  : isMissing
                    ? 'missing'
                    : isCompleted
                      ? 'completed'
                      : 'processing'
              }
            />
          );
        },
        sortable: true,
        valueFormatter: params =>
          formatters.formatContributionStatus(params.value),
        valueGetter: params =>
          params.row?.contributionMeta?.payrollDueStatus ||
          params.row?.contributionMeta?.processingStatus
      },
      {
        field: 'paygroup',
        flex: 1,
        headerName: 'Paygroup',
        minWidth: 100,
        renderCell: params => (
          <Stack>
            <Typography>
              {params.row?.contributionMeta?.payGroupName ?? '-'}
            </Typography>
            <Typography color='grey' variant='body2'>
              Code: {params.row?.contributionMeta?.division ?? 'null'}
            </Typography>
          </Stack>
        ),
        sortable: true,
        valueGetter: params => params.row?.contributionMeta?.payGroupName
      },
      {
        align: 'right',
        field: 'total',
        flex: 1,
        headerAlign: 'right',
        headerName: 'Total Amount',
        minWidth: 100,
        sortable: true,
        valueFormatter: params => formatters.formatDollars(params.value),
        valueGetter: params =>
          Decimal.sum(
            params.row?.totals?.total ?? 0,
            params.row?.contributionMeta?.correctionsTotal ?? 0,
            params.row?.contributionMeta?.matchRemediationsTotal ?? 0
          ).toNumber()
      },
      {
        align: 'right',
        field: 'totalEmployee',
        flex: 1,
        headerAlign: 'right',
        headerName: 'Employee',
        minWidth: 100,
        sortable: true,
        valueFormatter: params => formatters.formatDollars(params.value),
        valueGetter: params =>
          Decimal.sum(
            params.row?.totals?.totalSd ?? 0,
            params.row?.totals?.totalRc ?? 0,
            params.row?.totals?.totalAt ?? 0
          ).toNumber()
      },
      {
        align: 'right',
        field: 'totalCompany',
        flex: 1,
        headerAlign: 'right',
        headerName: 'Company',
        minWidth: 100,
        sortable: true,
        valueFormatter: params => formatters.formatDollars(params.value),
        valueGetter: params =>
          Decimal.sum(
            params.row?.totals?.totalEm ?? 0,
            params.row?.totals?.totalSh ?? 0,
            params.row?.totals?.totalPs ?? 0,
            params.row?.totals?.totalPw ?? 0,
            params.row?.totals?.totalQc ?? 0
          ).toNumber()
      },
      {
        align: 'right',
        field: 'totalLoan',
        flex: 1,
        headerAlign: 'right',
        headerName: 'Loan',
        minWidth: 100,
        sortable: true,
        valueFormatter: params => formatters.formatDollars(params.value),
        valueGetter: params => params.row?.totals?.totalLn
      },
      {
        field: 'actions',
        flex: 1,
        headerName: '',
        minWidth: 200,
        renderCell: params => {
          return (
            <SubmissionActions
              contribution={{ ...params.row, sponsorId: props.sponsorId }}
            />
          );
        },
        sortable: false
      }
    ],
    [props.sponsorId]
  );

  const rows = useMemo(
    () =>
      contributions.data
        ?.filter(contribution => !contribution.contributionMeta?.parentUcid)
        ?.map(contribution => ({
          ...contribution,
          id: uuidv4()
        })) ?? [],
    [contributions.data]
  );

  const { showOpsToolingDrawer } = useOpsTooling();

  const payrollSetupsQuery = usePayrollSetups(+props.sponsorPlanId);
  const isApiPlan = payrollSetupsQuery.data?.isApiPlan || false;

  const onClose = useCallback(() => {
    setAnchor(null);
  }, []);

  const handleOpsToolingMenuClick = useCallback(() => {
    showOpsToolingDrawer(+props.sponsorPlanId, OpsToolingType.payrollInfo);
    onClose();
  }, [props.sponsorPlanId]);

  return (
    <>
      {contributions.isError === undefined ? (
        <Typography data-testid='plan-contributions-error'>
          Error retrieving contributions
        </Typography>
      ) : (
        <Card variant='outlined'>
          <Box className={classes.titleBox}>
            <Typography variant='h5'>Contribution History</Typography>
            <Box display='flex'>
              <AccessControl
                requires={[FeatureLevelPermissions.WRITE_CONTRIBUTION]}>
                <StartOffCycleContribution
                  planId={props.sponsorPlanId}
                  sponsorId={props.sponsorId}
                />
              </AccessControl>
              <AccessControl
                requires={[FeatureLevelPermissions.READ_COMPANY_PAYROLL]}>
                {isApiPlan && (
                  <>
                    <IconButton
                      aria-controls={anchor ? 'ops-tooling-menu' : undefined}
                      aria-expanded={!!anchor}
                      aria-haspopup='true'
                      className={classes.anchorIcon}
                      data-testid='openMenuButton'
                      onClick={e => setAnchor(e.currentTarget)}
                      size='small'>
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={anchor}
                      id='ops-tooling-menu'
                      onClick={onClose}
                      onClose={onClose}
                      open={!!anchor}
                      transformOrigin={{
                        horizontal: 'right',
                        vertical: 'top'
                      }}>
                      <MenuItem
                        data-testid='openOpsToolingPayrolls'
                        onClick={handleOpsToolingMenuClick}>
                        View Payroll Provider Data - Payrolls
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </AccessControl>
            </Box>
          </Box>
          <Stack
            borderTop={1}
            display='flex'
            flexDirection='row'
            sx={{ borderColor: grey[300] }}>
            <ContributionsFilter />
            <DataGridPro
              autoHeight
              columns={columns}
              data-testid='plan-contributions-data'
              disableVirtualization={isTest}
              getDetailPanelContent={ContributionRowDetails}
              getDetailPanelHeight={() => 'auto'}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 25 } },
                sorting: {
                  sortModel: [
                    {
                      field: 'transaction',
                      sort: 'desc'
                    }
                  ]
                }
              }}
              loading={contributions.isLoading || contributions.isFetching}
              pageSizeOptions={[25, 50, 100]}
              pagination
              rows={rows}
              slots={{
                detailPanelCollapseIcon: KeyboardArrowDown,
                detailPanelExpandIcon: KeyboardArrowRight
              }}
              sortingOrder={['desc', 'asc']}
            />
          </Stack>
        </Card>
      )}
    </>
  );
};

export default PlanContributionsTab;
